import { Semaphore } from "../lib/semaphore.utils";

let cachedConfig: FirmwareConfig;
let lastFetchTime = 0;
const CACHE_TTL = 1000 * 60; // 1 minute

export interface FirmwareConfig {
  [key: string]: DeviceUpgradeConfig;
}

export type DeviceUpgradeConfig = SolarmanStickUpgradeConfig | InverterUpgradeConfig;

export type StickModelType = 'WIFI' | 'ETH';

export type StickModel = 'ZSM-WIFI-USB' | 'ZSM-WIFI-EXT' | 'ZSM-ETH-USB' | 'ZSM-ETH-EXT';

export interface SolarmanStickUpgradeConfig {
  ID: string;
  DEVICE_MODEL_ID: StickModel;
  LATEST_SOLARMAN_LOGGER?: string;
  LATEST_SOLARMAN_LOGGER_BY_WEB?: string;
  LINK_SOLARMAN_LOGGER?: string;
  MIN_SOLARMAN_LOGGER?: string;
  LAN_UPGRADE?: boolean;
}

export interface InverterUpgradeConfig {
  ID: string;
  DEVICE_MODEL_ID: 'INVERTER';
  MIN_SGP_DSPM?: string;
  MIN_SGP_DSPS?: string;
  MIN_SGP_ARM?: string;
  MIN_ONE_CLICK_ARM?: string;
  MODE: string[];
  ARM?: ChipData[];
  DSPM?: ChipData[];
  DSPS?: ChipData[];
  AFCI?: ChipData[];
  SOFAR?: SofarPackageData[];
  SAFETY?: ChipData[];
  SAFETY_CUSTOM?: ChipData[];
  BATTERY?: {
    BMS?: ChipData[];
    PCU?: ChipData[];
    BDU?: ChipData[];
  };
}

export interface ChipData {
  CHIP: string;
  LATEST_VERSION: string;
  LINK: string;
  MIN_VERSION: string;
  MODEL: string;
}

export interface SofarPackageData extends ChipData {
  ARM_VERSION?: string;
  DSPM_VERSION?: string;
  DSPS_VERSION?: string;
}

export const StickLoggerType = (type?: StickModel): StickModelType | undefined => {
  switch (type) {
    case 'ZSM-WIFI-USB':
    case 'ZSM-WIFI-EXT':
      return 'WIFI';

    case 'ZSM-ETH-USB':
    case 'ZSM-ETH-EXT':
      return 'ETH';

    default:
      return undefined;
  }
};

// Test-only bypass of the S3 PROD config. MUST stay false: with true, every
// app and every upgrade flow (legacy included) reads the local hard-coded
// config, whose bins may not exist on S3 yet. Flip locally for one-click
// verification sessions only — never commit it enabled.
const USE_LOCAL_CONFIG_FOR_ONE_CLICK_TEST = false;

export const _getFwUpgradeConfig = async (): Promise<FirmwareConfig> => {
  if (USE_LOCAL_CONFIG_FOR_ONE_CLICK_TEST) {
    return _upgradeConfig;
  }

  const firmwareConfigUrlBase = 'https://firmware-version-bucket.s3.eu-west-1.amazonaws.com/CONFIG/upgrade-config.json';

  if (!firmwareConfigUrlBase) {
    return _upgradeConfig;
  }

  const firmwareConfigUrl = `${firmwareConfigUrlBase}?v=${Date.now()}`;

  const now = Date.now();

  if (cachedConfig && lastFetchTime && (now - lastFetchTime < CACHE_TTL)) {
    return cachedConfig;
  }

  try {
    const response = await fetch(firmwareConfigUrl, {
      method: 'GET',
      cache: 'no-cache',
    });

    if (!response.ok) {
      return _upgradeConfig;
    }

    const externalConfig = (await response.json()) as FirmwareConfig;

    cachedConfig = externalConfig;
    lastFetchTime = now;

    return externalConfig;
  } catch (error) {
    console.warn('Failed to fetch firmware config', error);
    return _upgradeConfig;
  }
};

export const getFwUpgradeConfig = (new Semaphore()).wrap(_getFwUpgradeConfig);

const _upgradeConfig: FirmwareConfig = {
  "^lsw3_15_mqtt_270a([_.]|[0-9a-z])+$": {
    "ID": "LSW3_15_MQTT_270A",
    "DEVICE_MODEL_ID": "ZSM-WIFI-USB",
    "LATEST_SOLARMAN_LOGGER_BY_WEB": "LSW3_ZCSMQTT_270A_1.0C7",
    "LATEST_SOLARMAN_LOGGER": "LSW3_ZCSMQTT_270A_1.0C7",
    "LINK_SOLARMAN_LOGGER": "https://firmware-version-bucket.s3.eu-west-1.amazonaws.com/ZSM-WIFI-USB/LOGGER/LSW3_ZCSMQTT_270A_1.0C7_upgrade.bin",
    "MIN_SOLARMAN_LOGGER": "",
    "LAN_UPGRADE": true
  },
  "^lsw3_zcsmqtt_270a([_.]|[0-9a-z])+$": {
    "ID": "LSW3_ZCSMQTT_270A",
    "DEVICE_MODEL_ID": "ZSM-WIFI-USB",
    "LATEST_SOLARMAN_LOGGER_BY_WEB": "LSW3_ZCSMQTT_270A_1.0C7",
    "LATEST_SOLARMAN_LOGGER": "LSW3_ZCSMQTT_270A_1.0C7",
    "LINK_SOLARMAN_LOGGER": "https://firmware-version-bucket.s3.eu-west-1.amazonaws.com/ZSM-WIFI-USB/LOGGER/LSW3_ZCSMQTT_270A_1.0C7_upgrade.bin",
    "MIN_SOLARMAN_LOGGER": "",
    "LAN_UPGRADE": true
  },
  "^lsw3_15_270a([_.]|[1-9a-z])+$": {
    "ID": "LSW3_15_270A",
    "DEVICE_MODEL_ID": "ZSM-WIFI-USB",
    "LATEST_SOLARMAN_LOGGER_BY_WEB": "LSW3_ZCSMQTT_270A_1.0C7",
    "LATEST_SOLARMAN_LOGGER": "LSW3_ZCSMQTT_270A_1.0C7",
    "LINK_SOLARMAN_LOGGER": "https://firmware-version-bucket.s3.eu-west-1.amazonaws.com/ZSM-WIFI-USB/LOGGER/LSW3_ZCSMQTT_270A_1.0C7_upgrade.bin",
    "MIN_SOLARMAN_LOGGER": "",
    "LAN_UPGRADE": true
  },
  "^lsw3_15_ffff([_.]|[0-9a-z])+$": {
    "ID": "LSW3_15_FFFF",
    "DEVICE_MODEL_ID": "ZSM-WIFI-EXT",
    "LATEST_SOLARMAN_LOGGER": "LSW3_15_FFFF_1.0.96",
    "LINK_SOLARMAN_LOGGER": "https://firmware-version-bucket.s3.eu-west-1.amazonaws.com/ZSM-WIFI-EXT/LOGGER/LSW3_15_FFFF_1.0.96.bin",
    "MIN_SOLARMAN_LOGGER": "",
    "LAN_UPGRADE": true
  },
  "^me_0d_270a([_.]|[0-9a-z])+$": {
    "ID": "ME_0D_270A",
    "DEVICE_MODEL_ID": "ZSM-ETH-USB",
    "LATEST_SOLARMAN_LOGGER_BY_WEB": "ME_0E_ZCS_270A_1.0F",
    "LATEST_SOLARMAN_LOGGER": "ME_0E_ZCS_270A_1.0F",
    "LINK_SOLARMAN_LOGGER": "https://firmware-version-bucket.s3.eu-west-1.amazonaws.com/ZSM-ETH-USB/LOGGER/ME_0E_ZCS_270A_1.0F_UPGARDE.bin",
    "MIN_SOLARMAN_LOGGER": "",
    "LAN_UPGRADE": true
  },
  "^me_0e_zcs_270a([_.]|[0-9a-z])+$": {
    "ID": "ME_0E_ZCS_270A",
    "DEVICE_MODEL_ID": "ZSM-ETH-USB",
    "LATEST_SOLARMAN_LOGGER_BY_WEB": "ME_0E_ZCS_270A_1.15",
    "LATEST_SOLARMAN_LOGGER": "ME_0E_ZCS_270A_1.15",
    "LINK_SOLARMAN_LOGGER": "https://firmware-version-bucket.s3.eu-west-1.amazonaws.com/ZSM-ETH-USB/LOGGER/ME_0E_ZCS_270A_1.15_UPGARDE.bin",
    "MIN_SOLARMAN_LOGGER": "",
    "LAN_UPGRADE": true
  },
  "^me_0e_270a([_.]|[0-9a-z])+$": {
    "ID": "ME_0E_270A",
    "DEVICE_MODEL_ID": "ZSM-ETH-USB",
    "LATEST_SOLARMAN_LOGGER": "ME_0E_ZCS_270A_1.0F",
    "LATEST_SOLARMAN_LOGGER_BY_WEB": "ME_0E_ZCS_270A_1.0F",
    "LINK_SOLARMAN_LOGGER": "https://firmware-version-bucket.s3.eu-west-1.amazonaws.com/ZSM-ETH-USB/LOGGER/ME_0E_ZCS_270A_1.0F_UPGARDE.bin",
    "MIN_SOLARMAN_LOGGER": "",
    "LAN_UPGRADE": false
  },
  "^lse4wc_01_270a_zs_00_([0-9a-z])+$": {
    "ID": "LSE4WC_01_270A",
    "DEVICE_MODEL_ID": "ZSM-ETH-USB",
    "LATEST_SOLARMAN_LOGGER_BY_WEB": "LSE4WC_01_270A_ZS_00_0000000B",
    "LATEST_SOLARMAN_LOGGER": "LSE4WC_01_270A_ZS_00_0000000B",
    "LINK_SOLARMAN_LOGGER": "https://firmware-version-bucket.s3.eu-west-1.amazonaws.com/ZSM-ETH-USB/LOGGER/LSE4WC_01_270A_ZS_00_0000000B.bin",
    "MIN_SOLARMAN_LOGGER": "",
    "LAN_UPGRADE": true
  },
  "^me_0d_2701([_.]|[0-9a-z])+$": {
    "ID": "ME_0D_2701",
    "DEVICE_MODEL_ID": "ZSM-ETH-EXT",
    "LATEST_SOLARMAN_LOGGER": "ME_0D_2701_5.09",
    "LINK_SOLARMAN_LOGGER": "https://firmware-version-bucket.s3.eu-west-1.amazonaws.com/ZSM-ETH-EXT/LOGGER/ME_0D_2701_5.09.bin",
    "MIN_SOLARMAN_LOGGER": "",
    "LAN_UPGRADE": true
  },
  "^zh3.{11}$": {
    "ID": "ZH3",
    "MIN_SGP_DSPM": "V050004",
    "MIN_SGP_DSPS": "V050001",
    "MIN_SGP_ARM": "V050004",
    "ARM": [
      {
        "CHIP": "G0",
        "LATEST_VERSION": "G050004",
        "LINK": "https://firmware-version-bucket.s3.eu-west-1.amazonaws.com/ZH3/3-8KW-G3/ARM/3-8KW-G3_ARM_G0_G050004.bin",
        "MIN_VERSION": "G050002",
        "MODEL": "3-8KW-G3"
      },
      {
        "CHIP": "N1",
        "LATEST_VERSION": "V050004",
        "LINK": "https://firmware-version-bucket.s3.eu-west-1.amazonaws.com/ZH3/3-8KW-G3/ARM/3-8KW-G3_ARM_N1_V050004.bin",
        "MIN_VERSION": "V050002",
        "MODEL": "3-8KW-G3"
      }
    ],
    "DEVICE_MODEL_ID": "INVERTER",
    "DSPM": [
      {
        "CHIP": "T1",
        "LATEST_VERSION": "V050004",
        "LINK": "https://firmware-version-bucket.s3.eu-west-1.amazonaws.com/ZH3/3-8KW-G3/DSPM/3-8KW-G3_DSPM_T1_V050004.bin",
        "MIN_VERSION": "V050001",
        "MODEL": "3-8KW-G3"
      },
      {
        "CHIP": "U0",
        "LATEST_VERSION": "U050004",
        "LINK": "https://firmware-version-bucket.s3.eu-west-1.amazonaws.com/ZH3/3-8KW-G3/DSPM/3-8KW-G3_DSPM_U0_U050004.bin",
        "MIN_VERSION": "U050001",
        "MODEL": "3-8KW-G3"
      }
    ],
    "DSPS": [
      {
        "CHIP": "M0",
        "LATEST_VERSION": "P050001",
        "LINK": "https://firmware-version-bucket.s3.eu-west-1.amazonaws.com/ZH3/3-8KW-G3/DSPS/3-8KW-G3_DSPS_M0_P050001.bin",
        "MIN_VERSION": "P050001",
        "MODEL": "3-8KW-G3"
      },
      {
        "CHIP": "N4",
        "LATEST_VERSION": "V050001",
        "LINK": "https://firmware-version-bucket.s3.eu-west-1.amazonaws.com/ZH3/3-8KW-G3/DSPS/3-8KW-G3_DSPS_N4_V050001.bin",
        "MIN_VERSION": "V050001",
        "MODEL": "3-8KW-G3"
      }
    ],
    "MODE": [
      "DASHBOARD"
    ],
    "SAFETY": [
      {
        "CHIP": "",
        "LATEST_VERSION": "",
        "LINK": "https://firmware-version-bucket.s3.eu-west-1.amazonaws.com/ZH3/SAFETY/3-8KW-G3_SAFETY_240603160410.bin",
        "MIN_VERSION": "",
        "MODEL": ""
      }
    ],
    "SAFETY_CUSTOM": [
      {
        "CHIP": "",
        "LATEST_VERSION": "",
        "LINK": "https://firmware-version-bucket.s3.eu-west-1.amazonaws.com/ZH3/SAFETY/024-004-0903-9-1.txt",
        "MIN_VERSION": "",
        "MODEL": ""
      }
    ]
  },
  "^zj.{12}$": {
    "ID": "ZJ",
    "ARM": [],
    "DEVICE_MODEL_ID": "INVERTER",
    "DSPM": [],
    "DSPS": [],
    "MODE": [
      "DASHBOARD"
    ],
    "SAFETY": []
  },
  "^ze.{12}$": {
    "ID": "ZE",
    "ARM": [],
    "DEVICE_MODEL_ID": "INVERTER",
    "DSPM": [],
    "DSPS": [],
    "MODE": [
      "DASHBOARD"
    ],
    "SAFETY": []
  },
  "^zm2.{11}$": {
    "ID": "ZM2",
    "MIN_ONE_CLICK_ARM": "V070003",
    "MIN_SGP_DSPM": "V060012",
    "MIN_SGP_DSPS": "V060012",
    "MIN_SGP_ARM": "V060012",
    "ARM": [
      {
        "CHIP": "",
        "LATEST_VERSION": "V070004",
        "LINK": "https://firmware-version-bucket.s3.eu-west-1.amazonaws.com/ZM2/ARM/HYD-EP_ARM_V070004.bin",
        "MIN_VERSION": "V030073",
        "MODEL": ""
      }
    ],
    "DEVICE_MODEL_ID": "INVERTER",
    "DSPM": [
      {
        "CHIP": "",
        "LATEST_VERSION": "V070004",
        "LINK": "https://firmware-version-bucket.s3.eu-west-1.amazonaws.com/ZM2/DSPM/HYD-EP_DSPM_V070004.bin",
        "MIN_VERSION": "V030071",
        "MODEL": ""
      }
    ],
    "DSPS": [
      {
        "CHIP": "",
        "LATEST_VERSION": "V070004",
        "LINK": "https://firmware-version-bucket.s3.eu-west-1.amazonaws.com/ZM2/DSPS/HYD-EP_DSPS_V070004.bin",
        "MIN_VERSION": "V030000",
        "MODEL": ""
      }
    ],
    "MODE": [
      "LAN",
      "DASHBOARD"
    ],
    "SAFETY": [
      {
        "CHIP": "",
        "LATEST_VERSION": "",
        "LINK": "https://firmware-version-bucket.s3.eu-west-1.amazonaws.com/ZM2/SAFETY/HYD-EP_SAFETY_250303090509.bin",
        "MIN_VERSION": "",
        "MODEL": ""
      }
    ]
  },
  "^zp1.{5}([a-l][1-9a-c]|m[1-5]).{4}$": {
    "ID": "ZP1_OLD",
    "ARM": [
      {
        "CHIP": "",
        "LATEST_VERSION": "V110022",
        "LINK": "https://firmware-version-bucket.s3.eu-west-1.amazonaws.com/ZP1/ARM/ESHV_ARM_V110022.bin",
        "MIN_VERSION": "V100001",
        "MODEL": ""
      }
    ],
    "DEVICE_MODEL_ID": "INVERTER",
    "DSPM": [
      {
        "CHIP": "",
        "LATEST_VERSION": "V110022",
        "LINK": "https://firmware-version-bucket.s3.eu-west-1.amazonaws.com/ZP1/DSPM/ESHV_DSPM_V110022.bin",
        "MIN_VERSION": "V100003",
        "MODEL": ""
      }
    ],
    "DSPS": [
      {
        "CHIP": "",
        "LATEST_VERSION": "V110022",
        "LINK": "https://firmware-version-bucket.s3.eu-west-1.amazonaws.com/ZP1/DSPS/ESHV_DSPS_V110022.bin",
        "MIN_VERSION": "V100001",
        "MODEL": ""
      }
    ],
    "BATTERY": {
      "BDU": [
        {
          "CHIP": "",
          "LATEST_VERSION": "V010010",
          "LINK": "https://firmware-version-bucket.s3.eu-west-1.amazonaws.com/ZP1/BDU/BTS-5K_BDU_G2_V010010.bin",
          "MIN_VERSION": "",
          "MODEL": ""
        }
      ],
      "BMS": [
        {
          "CHIP": "",
          "LATEST_VERSION": "V010732",
          "LINK": "https://firmware-version-bucket.s3.eu-west-1.amazonaws.com/ZP1/BMS/HYD3-6K-HV_BMS_V010732.bin",
          "MIN_VERSION": "",
          "MODEL": ""
        }
      ],
      "PCU": [
        {
          "CHIP": "",
          "LATEST_VERSION": "V010732",
          "LINK": "https://firmware-version-bucket.s3.eu-west-1.amazonaws.com/ZP1/PCU/HYD3-6K-HV_PCU_V010732.bin",
          "MIN_VERSION": "",
          "MODEL": ""
        }
      ]
    },
    "MODE": [
      "LAN",
      "DASHBOARD"
    ],
    "SAFETY": [
      {
        "CHIP": "",
        "LATEST_VERSION": "",
        "LINK": "https://firmware-version-bucket.s3.eu-west-1.amazonaws.com/ZP1/SAFETY/ESHV_SAFETY_250108154316.bin",
        "MIN_VERSION": "",
        "MODEL": ""
      }
    ],
    "SAFETY_CUSTOM": [
      {
        "CHIP": "",
        "LATEST_VERSION": "",
        "LINK": "https://firmware-version-bucket.s3.eu-west-1.amazonaws.com/ZP1/SAFETY/1003/024-004-1003.txt",
        "MIN_VERSION": "",
        "MODEL": ""
      }
    ]
  },
  "^zp1.{5}(m[6-9a-c]|[n-z][1-9a-c]).{4}$": {
    "ID": "ZP1",
    "MIN_ONE_CLICK_ARM": "V120002",
    "MIN_SGP_DSPM": "V1100051",
    "MIN_SGP_DSPS": "V1100051",
    "MIN_SGP_ARM": "V1100051",
    "ARM": [
      {
        "CHIP": "",
        "LATEST_VERSION": "V120005",
        "LINK": "https://firmware-version-bucket.s3.eu-west-1.amazonaws.com/ZP1/ARM/ESHV_ARM_V120005.bin",
        "MIN_VERSION": "V100001",
        "MODEL": ""
      }
    ],
    "DEVICE_MODEL_ID": "INVERTER",
    "DSPM": [
      {
        "CHIP": "",
        "LATEST_VERSION": "V120005",
        "LINK": "https://firmware-version-bucket.s3.eu-west-1.amazonaws.com/ZP1/DSPM/ESHV_DSPM_V120005.bin",
        "MIN_VERSION": "V100003",
        "MODEL": ""
      }
    ],
    "DSPS": [
      {
        "CHIP": "",
        "LATEST_VERSION": "V120005",
        "LINK": "https://firmware-version-bucket.s3.eu-west-1.amazonaws.com/ZP1/DSPS/ESHV_DSPS_V120005.bin",
        "MIN_VERSION": "V100001",
        "MODEL": ""
      }
    ],
    "BATTERY": {
      "BDU": [
        {
          "CHIP": "",
          "LATEST_VERSION": "V010010",
          "LINK": "https://firmware-version-bucket.s3.eu-west-1.amazonaws.com/ZP1/BDU/BTS-5K_BDU_G2_V010010.bin",
          "MIN_VERSION": "",
          "MODEL": ""
        }
      ],
      "BMS": [
        {
          "CHIP": "",
          "LATEST_VERSION": "V010732",
          "LINK": "https://firmware-version-bucket.s3.eu-west-1.amazonaws.com/ZP1/BMS/HYD3-6K-HV_BMS_V010732.bin",
          "MIN_VERSION": "",
          "MODEL": ""
        }
      ],
      "PCU": [
        {
          "CHIP": "",
          "LATEST_VERSION": "V010732",
          "LINK": "https://firmware-version-bucket.s3.eu-west-1.amazonaws.com/ZP1/PCU/HYD3-6K-HV_PCU_V010732.bin",
          "MIN_VERSION": "",
          "MODEL": ""
        }
      ]
    },
    "MODE": [
      "LAN",
      "DASHBOARD"
    ],
    "SAFETY": [
      {
        "CHIP": "",
        "LATEST_VERSION": "",
        "LINK": "https://firmware-version-bucket.s3.eu-west-1.amazonaws.com/ZP1/SAFETY/ESHV_SAFETY_250108154316.bin",
        "MIN_VERSION": "",
        "MODEL": ""
      }
    ],
    "SAFETY_CUSTOM": [
      {
        "CHIP": "",
        "LATEST_VERSION": "",
        "LINK": "https://firmware-version-bucket.s3.eu-west-1.amazonaws.com/ZP1/SAFETY/1003/024-004-1003.txt",
        "MIN_VERSION": "",
        "MODEL": ""
      }
    ]
  },
  "^zp2.{11}$": {
    "ID": "ZP2",
    "MIN_ONE_CLICK_ARM": "V120002",
    "MIN_SGP_DSPM": "V1100051",
    "MIN_SGP_DSPS": "V1100051",
    "MIN_SGP_ARM": "V1100051",
    "ARM": [
      {
        "CHIP": "",
        "LATEST_VERSION": "V120005",
        "LINK": "https://firmware-version-bucket.s3.eu-west-1.amazonaws.com/ZP1/ARM/ESHV_ARM_V120005.bin",
        "MIN_VERSION": "V100001",
        "MODEL": ""
      }
    ],
    "DEVICE_MODEL_ID": "INVERTER",
    "DSPM": [
      {
        "CHIP": "",
        "LATEST_VERSION": "V120005",
        "LINK": "https://firmware-version-bucket.s3.eu-west-1.amazonaws.com/ZP1/DSPM/ESHV_DSPM_V120005.bin",
        "MIN_VERSION": "V100003",
        "MODEL": ""
      }
    ],
    "DSPS": [
      {
        "CHIP": "",
        "LATEST_VERSION": "V120005",
        "LINK": "https://firmware-version-bucket.s3.eu-west-1.amazonaws.com/ZP1/DSPS/ESHV_DSPS_V120005.bin",
        "MIN_VERSION": "V100001",
        "MODEL": ""
      }
    ],
    "BATTERY": {
      "BDU": [
        {
          "CHIP": "",
          "LATEST_VERSION": "V010010",
          "LINK": "https://firmware-version-bucket.s3.eu-west-1.amazonaws.com/ZP1/BDU/BTS-5K_BDU_G2_V010010.bin",
          "MIN_VERSION": "",
          "MODEL": ""
        }
      ],
      "BMS": [
        {
          "CHIP": "",
          "LATEST_VERSION": "V010732",
          "LINK": "https://firmware-version-bucket.s3.eu-west-1.amazonaws.com/ZP1/BMS/HYD3-6K-HV_BMS_V010732.bin",
          "MIN_VERSION": "",
          "MODEL": ""
        }
      ],
      "PCU": [
        {
          "CHIP": "",
          "LATEST_VERSION": "V010732",
          "LINK": "https://firmware-version-bucket.s3.eu-west-1.amazonaws.com/ZP1/PCU/HYD3-6K-HV_PCU_V010732.bin",
          "MIN_VERSION": "",
          "MODEL": ""
        }
      ]
    },
    "MODE": [
      "LAN",
      "DASHBOARD"
    ],
    "SAFETY": [
      {
        "CHIP": "",
        "LATEST_VERSION": "",
        "LINK": "https://firmware-version-bucket.s3.eu-west-1.amazonaws.com/ZP1/SAFETY/ESHV_SAFETY_250108154316.bin",
        "MIN_VERSION": "",
        "MODEL": ""
      }
    ],
    "SAFETY_CUSTOM": [
      {
        "CHIP": "",
        "LATEST_VERSION": "",
        "LINK": "https://firmware-version-bucket.s3.eu-west-1.amazonaws.com/ZP1/SAFETY/1003/024-004-1003.txt",
        "MIN_VERSION": "",
        "MODEL": ""
      }
    ]
  },
  "^z.1057.{14}$": {
    "ID": "Z*1057",
    "MIN_ONE_CLICK_ARM": "V000002",
    "MIN_SGP_DSPM": "V000003",
    "MIN_SGP_DSPS": "V000003",
    "MIN_SGP_ARM": "V000003",
    "ARM": [
      {
        "CHIP": "",
        "LATEST_VERSION": "V000010",
        "LINK": "https://firmware-version-bucket.s3.eu-west-1.amazonaws.com/ZH1057/ARM/ESI-5_6_6.5_8_9.9_10_12K-T1_ARM_V000010.bin",
        "MIN_VERSION": "V000001",
        "MODEL": ""
      }
    ],
    "DEVICE_MODEL_ID": "INVERTER",
    "DSPM": [
      {
        "CHIP": "",
        "LATEST_VERSION": "V000010",
        "LINK": "https://firmware-version-bucket.s3.eu-west-1.amazonaws.com/ZH1057/DSPM/ESI-5_6_6.5_8_9.9_10_12K-T1_DSPM_V000010.bin",
        "MIN_VERSION": "V000001",
        "MODEL": ""
      }
    ],
    "DSPS": [
      {
        "CHIP": "",
        "LATEST_VERSION": "V000010",
        "LINK": "https://firmware-version-bucket.s3.eu-west-1.amazonaws.com/ZH1057/DSPS/ESI-5_6_6.5_8_9.9_10_12K-T1_DSPS_V000010.bin",
        "MIN_VERSION": "V000001",
        "MODEL": ""
      }
    ],
    "AFCI": [
      {
        "CHIP": "",
        "LATEST_VERSION": "V000010",
        "LINK": "https://firmware-version-bucket.s3.eu-west-1.amazonaws.com/ZH1057/AFCI/ESI-5_6_6.5_8_9.9_10_12K-T1_AFCI_V000010.bin",
        "MIN_VERSION": "V000001",
        "MODEL": ""
      }
    ],
    "BATTERY": {
      "BMS": [
        {
          "CHIP": "",
          "LATEST_VERSION": "V010732",
          "LINK": "https://firmware-version-bucket.s3.eu-west-1.amazonaws.com/ZP1/BMS/HYD3-6K-HV_BMS_V010732.bin",
          "MIN_VERSION": "",
          "MODEL": ""
        }
      ],
      "PCU": [
        {
          "CHIP": "",
          "LATEST_VERSION": "V010732",
          "LINK": "https://firmware-version-bucket.s3.eu-west-1.amazonaws.com/ZP1/PCU/HYD3-6K-HV_PCU_V010732.bin",
          "MIN_VERSION": "",
          "MODEL": ""
        }
      ]
    },
    "MODE": [
      "LAN",
      "DASHBOARD"
    ],
    "SAFETY": [
      {
        "CHIP": "",
        "LATEST_VERSION": "",
        "LINK": "https://firmware-version-bucket.s3.eu-west-1.amazonaws.com/ZH1057/SAFETY/ESI-5_6_6.5_8_9.9_10_12K-T1_SAFETY_260430144037.bin",
        "MIN_VERSION": "",
        "MODEL": ""
      }
    ],
    "SAFETY_CUSTOM": [
      {
        "CHIP": "",
        "LATEST_VERSION": "",
        "LINK": "https://firmware-version-bucket.s3.eu-west-1.amazonaws.com/ZH1057/SAFETY/1014/024-004-1014.txt",
        "MIN_VERSION": "",
        "MODEL": ""
      }
    ]
  },
  "^zm1.{5}[a-k][1-9a-z].{4}$": {
    "ID": "ZM1",
    "ARM": [],
    "DEVICE_MODEL_ID": "INVERTER",
    "DSPM": [],
    "DSPS": [],
    "MODE": [
      "LAN",
      "DASHBOARD"
    ],
    "SAFETY": []
  },
  "^zm1.{5}[l-z][1-9a-z].{4}$": {
    "ID": "ZM1",
    "ARM": [],
    "DEVICE_MODEL_ID": "INVERTER",
    "DSPM": [],
    "DSPS": [],
    "MODE": [
      "LAN",
      "DASHBOARD"
    ],
    "SAFETY": []
  },
  "^zq1.{11}$": {
    "ID": "ZQ1",
    "MIN_SGP_DSPM": "V050000",
    "MIN_SGP_DSPS": "V050000",
    "MIN_SGP_ARM": "V050000",
    "ARM": [
      {
        "CHIP": "N2",
        "LATEST_VERSION": "V050001",
        "LINK": "https://firmware-version-bucket.s3.eu-west-1.amazonaws.com/ZQ1/ARM/100KW_ARM_V050001.bin",
        "MIN_VERSION": "V040011",
        "MODEL": "100KW-G3"
      }
    ],
    "DEVICE_MODEL_ID": "INVERTER",
    "DSPM": [
      {
        "CHIP": "T0",
        "LATEST_VERSION": "V050000",
        "LINK": "https://firmware-version-bucket.s3.eu-west-1.amazonaws.com/ZQ1/DSPM/100KW_DSPM_V050000.bin",
        "MIN_VERSION": "V040006",
        "MODEL": "100KW-G3"
      }
    ],
    "DSPS": [
      {
        "CHIP": "T1",
        "LATEST_VERSION": "V050000",
        "LINK": "https://firmware-version-bucket.s3.eu-west-1.amazonaws.com/ZQ1/DSPS/100KW_DSPS_V050000.bin",
        "MIN_VERSION": "V040006",
        "MODEL": "100KW-G3"
      }
    ],
    "MODE": [
      "LAN",
      "DASHBOARD"
    ],
    "SAFETY": [
      {
        "CHIP": "",
        "LATEST_VERSION": "",
        "LINK": "https://firmware-version-bucket.s3.eu-west-1.amazonaws.com/ZQ1/SAFETY/100KW-G3_SAFETY_240710194658.bin",
        "MIN_VERSION": "",
        "MODEL": ""
      }
    ]
  },
  "^zh2008.{14}$": {
    "ID": "ZH008",
    "MIN_ONE_CLICK_ARM": "V000032",
    "ARM": [
      {
        "CHIP": "",
        "LATEST_VERSION": "V000033",
        "LINK": "https://firmware-version-bucket.s3.eu-west-1.amazonaws.com/ZH2008/ARM/BT5000A_ARM_V000033.bin",
        "MIN_VERSION": "V000000",
        "MODEL": ""
      }
    ],
    "DSPM": [
      {
        "CHIP": "",
        "LATEST_VERSION": "V000033",
        "LINK": "https://firmware-version-bucket.s3.eu-west-1.amazonaws.com/ZH2008/DSPM/BT5000A_DSPM_V000033.bin",
        "MIN_VERSION": "V000000",
        "MODEL": ""
      }
    ],
    "DSPS": [
      {
        "CHIP": "",
        "LATEST_VERSION": "V000033",
        "LINK": "https://firmware-version-bucket.s3.eu-west-1.amazonaws.com/ZH2008/DSPS/BT5000A_DSPS_V000033.bin",
        "MIN_VERSION": "V000000",
        "MODEL": ""
      }
    ],
    "DEVICE_MODEL_ID": "INVERTER",
    "MODE": [
      "LAN",
      "DASHBOARD"
    ],
    "SAFETY": [
      {
        "CHIP": "",
        "LATEST_VERSION": "",
        "LINK": "https://firmware-version-bucket.s3.eu-west-1.amazonaws.com/ZH2008/SAFETY/BT5000A_SAFETY_241211104609.bin",
        "MIN_VERSION": "",
        "MODEL": ""
      }
    ]
  },
  "^zs2.{11}$": {
    "ID": "ZS2",
    "MIN_SGP_DSPM": "V110008",
    "MIN_SGP_DSPS": "L110001",
    "MIN_SGP_ARM": "V110006",
    "ARM": [
      {
        "CHIP": "N1",
        "LATEST_VERSION": "V110006",
        "LINK": "https://firmware-version-bucket.s3.eu-west-1.amazonaws.com/ZS2/ARM/3-12KW-G3_ARM_LPC_V110006.bin",
        "MIN_VERSION": "V000000",
        "MODEL": ""
      },
      {
        "CHIP": "M1",
        "LATEST_VERSION": "I110006",
        "LINK": "https://firmware-version-bucket.s3.eu-west-1.amazonaws.com/ZS2/ARM/3-12KW-G3_ARM_MSP_I110006.bin",
        "MIN_VERSION": "I000000",
        "MODEL": ""
      },
      {
        "CHIP": "G0",
        "LATEST_VERSION": "G110006",
        "LINK": "https://firmware-version-bucket.s3.eu-west-1.amazonaws.com/ZS2/ARM/3-12KW-G3_ARM_STM_G110006.bin",
        "MIN_VERSION": "G000000",
        "MODEL": ""
      }
    ],
    "DEVICE_MODEL_ID": "INVERTER",
    "DSPM": [
      {
        "CHIP": "T1",
        "LATEST_VERSION": "V110008",
        "LINK": "https://firmware-version-bucket.s3.eu-west-1.amazonaws.com/ZS2/DSPM/3-12KW-G3_DSPM_TMS_V110008.bin",
        "MIN_VERSION": "V000000",
        "MODEL": ""
      }
    ],
    "DSPS": [
      {
        "CHIP": "N3",
        "LATEST_VERSION": "L110001",
        "LINK": "https://firmware-version-bucket.s3.eu-west-1.amazonaws.com/ZS2/DSPS/3-12KW-G3_DSPS_LPC802_L110001.bin",
        "MIN_VERSION": "L000000",
        "MODEL": ""
      },
      {
        "CHIP": "N4",
        "LATEST_VERSION": "V110001",
        "LINK": "https://firmware-version-bucket.s3.eu-west-1.amazonaws.com/ZS2/DSPS/3-12KW-G3_DSPS_LPC804_V110001.bin",
        "MIN_VERSION": "V000000",
        "MODEL": ""
      }
    ],
    "MODE": [
      "DASHBOARD"
    ],
    "SAFETY": [
      {
        "CHIP": "",
        "LATEST_VERSION": "",
        "LINK": "https://firmware-version-bucket.s3.eu-west-1.amazonaws.com/ZS2/SAFETY/3-12KW-G3_SAFETY_240726100439.bin",
        "MIN_VERSION": "",
        "MODEL": ""
      }
    ],
    "SAFETY_CUSTOM": [
      {
        "CHIP": "",
        "LATEST_VERSION": "",
        "LINK": "https://firmware-version-bucket.s3.eu-west-1.amazonaws.com/ZH3/SAFETY/024-004-0903-9-1.txt",
        "MIN_VERSION": "",
        "MODEL": ""
      }
    ]
  },
  "^za1.{11}$": {
    "ID": "ZA1",
    "ARM": [],
    "DEVICE_MODEL_ID": "INVERTER",
    "DSPM": [],
    "DSPS": [],
    "MODE": [
      "DASHBOARD"
    ],
    "SAFETY": []
  },
  "^zs3.{11}$": {
    "ID": "ZS3",
    "MIN_SGP_DSPM": "V050002",
    "MIN_SGP_DSPS": "L050001",
    "MIN_SGP_ARM": "I050002",
    "ARM": [
      {
        "CHIP": "M1",
        "LATEST_VERSION": "I060014",
        "LINK": "https://firmware-version-bucket.s3.eu-west-1.amazonaws.com/ZS3/ARM/25-50KW-G3_ARM_I060014.bin",
        "MIN_VERSION": "I040024",
        "MODEL": ""
      }
    ],
    "DEVICE_MODEL_ID": "INVERTER",
    "DSPM": [
      {
        "CHIP": "T1",
        "LATEST_VERSION": "V060009",
        "LINK": "https://firmware-version-bucket.s3.eu-west-1.amazonaws.com/ZS3/DSPM/25-50KW-G3_DSPM_V060009.bin",
        "MIN_VERSION": "V040011",
        "MODEL": ""
      }
    ],
    "DSPS": [
      {
        "CHIP": "N3",
        "LATEST_VERSION": "L050001",
        "LINK": "https://firmware-version-bucket.s3.eu-west-1.amazonaws.com/ZS3/DSPS/25-50KW-G3_DSPS_L050001.bin",
        "MIN_VERSION": "L040002",
        "MODEL": ""
      }
    ],
    "AFCI": [
      {
        "CHIP": "BT1",
        "LATEST_VERSION": "V000001",
        "LINK": "https://firmware-version-bucket.s3.eu-west-1.amazonaws.com/ZS3/AFCI/25-50KW-G3_AFCI_TMS320F280049C_V000001.bin",
        "MIN_VERSION": "V000001",
        "MODEL": ""
      }
    ],
    "MODE": [
      "DASHBOARD"
    ],
    "SAFETY": [
      {
        "CHIP": "",
        "LATEST_VERSION": "",
        "LINK": "https://firmware-version-bucket.s3.eu-west-1.amazonaws.com/ZS3/SAFETY/25-50KW-G3_SAFETY_250214142040.bin",
        "MIN_VERSION": "",
        "MODEL": ""
      }
    ],
    "SAFETY_CUSTOM": [
      {
        "CHIP": "",
        "LATEST_VERSION": "",
        "LINK": "https://firmware-version-bucket.s3.eu-west-1.amazonaws.com/ZH3/SAFETY/024-004-0903-9-1.txt",
        "MIN_VERSION": "",
        "MODEL": ""
      }
    ]
  },
  "^zs1.{11}$": {
    "ID": "ZS1",
    "MIN_SGP_DSPM": "V110008",
    "MIN_SGP_DSPS": "V110002",
    "MIN_SGP_ARM": "V110010",
    "ARM": [
      {
        "CHIP": "N1",
        "LATEST_VERSION": "V110010",
        "LINK": "https://firmware-version-bucket.s3.eu-west-1.amazonaws.com/ZS1/ARM/3-20KW-G3_ARM_N1_V110010.bin",
        "MIN_VERSION": "V000000",
        "MODEL": ""
      },
      {
        "CHIP": "G0",
        "LATEST_VERSION": "G110010",
        "LINK": "https://firmware-version-bucket.s3.eu-west-1.amazonaws.com/ZS1/ARM/3-20KW-G3_ARM_G0_G110010.bin",
        "MIN_VERSION": "G000000",
        "MODEL": ""
      }
    ],
    "DEVICE_MODEL_ID": "INVERTER",
    "DSPM": [
      {
        "CHIP": "T1",
        "LATEST_VERSION": "V110008",
        "LINK": "https://firmware-version-bucket.s3.eu-west-1.amazonaws.com/ZS1/DSPM/3-20KW-G3_DSPM_TMS_V110008.bin",
        "MIN_VERSION": "V000000",
        "MODEL": ""
      }
    ],
    "DSPS": [
      {
        "CHIP": "N4",
        "LATEST_VERSION": "V110002",
        "LINK": "https://firmware-version-bucket.s3.eu-west-1.amazonaws.com/ZS1/DSPS/3-20KW-G3_DSPS_LPC804_V110002.bin",
        "MIN_VERSION": "V000000",
        "MODEL": ""
      }
    ],
    "MODE": [
      "LAN",
      "DASHBOARD"
    ],
    "SAFETY": [
      {
        "CHIP": "",
        "LATEST_VERSION": "",
        "LINK": "https://firmware-version-bucket.s3.eu-west-1.amazonaws.com/ZS1/SAFETY/3-20KW-G3_SAFETY_241015144807.bin",
        "MIN_VERSION": "",
        "MODEL": ""
      }
    ],
    "SAFETY_CUSTOM": [
      {
        "CHIP": "",
        "LATEST_VERSION": "",
        "LINK": "https://firmware-version-bucket.s3.eu-west-1.amazonaws.com/ZH3/SAFETY/024-004-0903-9-1.txt",
        "MIN_VERSION": "",
        "MODEL": ""
      }
    ]
  },
  "^zr1.{11}$": {
    "ID": "ZR1",
    "MIN_SGP_DSPM": "V020005",
    "MIN_SGP_DSPS": "V020005",
    "MIN_SGP_ARM": "V020005",
    "ARM": [
      {
        "CHIP": "N2",
        "LATEST_VERSION": "V070000",
        "LINK": "https://firmware-version-bucket.s3.eu-west-1.amazonaws.com/ZR1/ARM/225KW_ARM_V070000.bin",
        "MIN_VERSION": "V020001",
        "MODEL": "225KW-G3"
      }
    ],
    "DEVICE_MODEL_ID": "INVERTER",
    "DSPM": [
      {
        "CHIP": "T0",
        "LATEST_VERSION": "V070000",
        "LINK": "https://firmware-version-bucket.s3.eu-west-1.amazonaws.com/ZR1/DSPM/225KW_DSPM_V070000.bin",
        "MIN_VERSION": "V020001",
        "MODEL": "225KW-G3"
      }
    ],
    "DSPS": [
      {
        "CHIP": "T1",
        "LATEST_VERSION": "V070000",
        "LINK": "https://firmware-version-bucket.s3.eu-west-1.amazonaws.com/ZR1/DSPS/225KW_DSPS_V070000.bin",
        "MIN_VERSION": "V020001",
        "MODEL": "225KW-G3"
      }
    ],
    "MODE": [
      "DASHBOARD"
    ],
    "SAFETY": [
      {
        "CHIP": "",
        "LATEST_VERSION": "",
        "LINK": "https://firmware-version-bucket.s3.eu-west-1.amazonaws.com/ZR1/SAFETY/225KW-G3_SAFETY_251121184318.bin",
        "MIN_VERSION": "",
        "MODEL": ""
      }
    ]
  },
  "^z.1036.{14}$": {
    "ID": "Z*1036",
    "MIN_SGP_DSPM": "V020003",
    "MIN_SGP_DSPS": "V020003",
    "MIN_SGP_ARM": "V020003",
    "ARM": [
      {
        "CHIP": "A1",
        "LATEST_VERSION": "V600009",
        "LINK": "https://firmware-version-bucket.s3.eu-west-1.amazonaws.com/ZD1036/ARM/550.01000028-0_ARM_A1_V600009_APP.bin",
        "MIN_VERSION": "V100000",
        "MODEL": "125KW-G4"
      }
    ],
    "DEVICE_MODEL_ID": "INVERTER",
    "DSPM": [
      {
        "CHIP": "T0",
        "LATEST_VERSION": "V600008",
        "LINK": "https://firmware-version-bucket.s3.eu-west-1.amazonaws.com/ZD1036/DSPM/550.01000022-0_DSPM_T0_V600008_APP.bin",
        "MIN_VERSION": "V100000",
        "MODEL": "125KW-G4"
      }
    ],
    "DSPS": [
      {
        "CHIP": "T1",
        "LATEST_VERSION": "V600005",
        "LINK": "https://firmware-version-bucket.s3.eu-west-1.amazonaws.com/ZD1036/DSPS/550.01000024-0_DSPS_T1_V600005_APP.bin",
        "MIN_VERSION": "V100000",
        "MODEL": "125KW-G4"
      }
    ],
    "AFCI": [
      {
        "CHIP": "T1",
        "LATEST_VERSION": "V07",
        "LINK": "https://firmware-version-bucket.s3.eu-west-1.amazonaws.com/ZD1036/AFCI/550.01000026-0_AFCI_T1_V07_APP.bin",
        "MIN_VERSION": "V100001",
        "MODEL": "125KW-G4"
      }
    ],
    "MODE": [
      "LAN",
      "DASHBOARD"
    ],
    "SAFETY": [
      {
        "CHIP": "",
        "LATEST_VERSION": "",
        "LINK": "https://firmware-version-bucket.s3.eu-west-1.amazonaws.com/ZD1036/SAFETY/125KW-G4_SAFETY_250909171829.bin",
        "MIN_VERSION": "",
        "MODEL": ""
      }
    ]
  },
  "^z.1021.{14}$": {
    "ID": "Z*1021",
    "MIN_SGP_DSPM": "V040005",
    "MIN_SGP_DSPS": "V040005",
    "MIN_SGP_ARM": "V040005",
    "ARM": [
      {
        "CHIP": "M1",
        "LATEST_VERSION": "V040009",
        "LINK": "https://firmware-version-bucket.s3.eu-west-1.amazonaws.com/ZD1021/ARM/550.01021101-0_ARM_M1_V040009_APP.bin",
        "MIN_VERSION": "V000012",
        "MODEL": "60-80KW-G3"
      }
    ],
    "DEVICE_MODEL_ID": "INVERTER",
    "DSPM": [
      {
        "CHIP": "T0",
        "LATEST_VERSION": "V040008",
        "LINK": "https://firmware-version-bucket.s3.eu-west-1.amazonaws.com/ZD1021/DSPM/550.01021004-0_DSPM_T0_V040008_APP.bin",
        "MIN_VERSION": "V000016",
        "MODEL": "60-80KW-G3"
      }
    ],
    "DSPS": [
      {
        "CHIP": "T1",
        "LATEST_VERSION": "V040005",
        "LINK": "https://firmware-version-bucket.s3.eu-west-1.amazonaws.com/ZD1021/DSPS/550.01021005-0_DSPS_T1_V040005_APP.bin",
        "MIN_VERSION": "V000016",
        "MODEL": "60-80KW-G3"
      }
    ],
    "MODE": [
      "LAN",
      "DASHBOARD"
    ],
    "SAFETY": [
      {
        "CHIP": "",
        "LATEST_VERSION": "",
        "LINK": "https://firmware-version-bucket.s3.eu-west-1.amazonaws.com/ZD1021/SAFETY/60-80KW-G3_SAFETY_250911151930.bin",
        "MIN_VERSION": "",
        "MODEL": ""
      }
    ]
  },
  "^z.1030.{14}$": {
    "ID": "Z*1030",
    "MIN_SGP_DSPM": "V010605",
    "MIN_SGP_DSPS": "V010605",
    "MIN_SGP_ARM": "V010605",
    "ARM": [],
    "DEVICE_MODEL_ID": "INVERTER",
    "DSPM": [],
    "DSPS": [],
    "MODE": [
      "LAN",
      "DASHBOARD"
    ],
    "SAFETY": [
      {
        "CHIP": "",
        "LATEST_VERSION": "",
        "LINK": "https://firmware-version-bucket.s3.eu-west-1.amazonaws.com/ZD1030/SAFETY/320KW_SAFETY_bin_250421195958_0xff.bin",
        "MIN_VERSION": "",
        "MODEL": ""
      }
    ]
  },
  "^z.1025.{14}$": {
    "ID": "Z*1025",
    "MIN_ONE_CLICK_ARM": "V050002",
    "MIN_SGP_DSPM": "V600005",
    "MIN_SGP_DSPS": "V600005",
    "MIN_SGP_ARM": "V600005",
    "ARM": [
      {
        "CHIP": "N2",
        "LATEST_VERSION": "V050023",
        "LINK": "https://firmware-version-bucket.s3.eu-west-1.amazonaws.com/ZD1025/ARM/HYD3-6K-HV_ARM_N2_V050023.bin",
        "MIN_VERSION": "V000000",
        "MODEL": "HYD3-6K-HV"
      }
    ],
    "DEVICE_MODEL_ID": "INVERTER",
    "DSPM": [
      {
        "CHIP": "D0",
        "LATEST_VERSION": "V050023",
        "LINK": "https://firmware-version-bucket.s3.eu-west-1.amazonaws.com/ZD1025/DSPM/HYD3-6K-HV_DSPM_D0_V050023.bin",
        "MIN_VERSION": "V000000",
        "MODEL": "HYD3-6K-HV"
      }
    ],
    "DSPS": [
      {
        "CHIP": "N4",
        "LATEST_VERSION": "V050023",
        "LINK": "https://firmware-version-bucket.s3.eu-west-1.amazonaws.com/ZD1025/DSPS/HYD3-6K-HV_DSPS_N4_V050023.bin",
        "MIN_VERSION": "V000000",
        "MODEL": "HYD3-6K-HV"
      }
    ],
    "BATTERY": {
      "BMS": [
        {
          "CHIP": "",
          "LATEST_VERSION": "V010732",
          "LINK": "https://firmware-version-bucket.s3.eu-west-1.amazonaws.com/ZP1/BMS/HYD3-6K-HV_BMS_V010732.bin",
          "MIN_VERSION": "",
          "MODEL": ""
        }
      ],
      "PCU": [
        {
          "CHIP": "",
          "LATEST_VERSION": "V010732",
          "LINK": "https://firmware-version-bucket.s3.eu-west-1.amazonaws.com/ZP1/PCU/HYD3-6K-HV_PCU_V010732.bin",
          "MIN_VERSION": "",
          "MODEL": ""
        }
      ]
    },
    "MODE": [
      "LAN",
      "DASHBOARD"
    ],
    "SAFETY_CUSTOM": [
      {
        "CHIP": "",
        "LATEST_VERSION": "",
        "LINK": "https://firmware-version-bucket.s3.eu-west-1.amazonaws.com/ZD1025/SAFETY/951/001-000-951.txt",
        "MIN_VERSION": "",
        "MODEL": ""
      },
      {
        "CHIP": "",
        "LATEST_VERSION": "",
        "LINK": "https://firmware-version-bucket.s3.eu-west-1.amazonaws.com/ZD1025/SAFETY/951/001-003-951.txt",
        "MIN_VERSION": "",
        "MODEL": ""
      },
      {
        "CHIP": "",
        "LATEST_VERSION": "",
        "LINK": "https://firmware-version-bucket.s3.eu-west-1.amazonaws.com/ZD1025/SAFETY/1012/024-004-1012.txt",
        "MIN_VERSION": "",
        "MODEL": ""
      }
    ],
    "SAFETY": [
      {
        "CHIP": "",
        "LATEST_VERSION": "",
        "LINK": "https://firmware-version-bucket.s3.eu-west-1.amazonaws.com/ZD1025/SAFETY/HYD3-6K-HV_SAFETY_251104154428.bin",
        "MIN_VERSION": "",
        "MODEL": ""
      }
    ]
  },
  "^z.1050.{14}$": {
    "ID": "Z*1050",
    "MIN_ONE_CLICK_ARM": "V070003",
    "MIN_SGP_DSPM": "V060012",
    "MIN_SGP_DSPS": "V060012",
    "MIN_SGP_ARM": "V060012",
    "ARM": [
      {
        "CHIP": "",
        "LATEST_VERSION": "V070004",
        "LINK": "https://firmware-version-bucket.s3.eu-west-1.amazonaws.com/ZM2/ARM/HYD-EP_ARM_V070004.bin",
        "MIN_VERSION": "V030073",
        "MODEL": ""
      }
    ],
    "DEVICE_MODEL_ID": "INVERTER",
    "DSPM": [
      {
        "CHIP": "",
        "LATEST_VERSION": "V070004",
        "LINK": "https://firmware-version-bucket.s3.eu-west-1.amazonaws.com/ZM2/DSPM/HYD-EP_DSPM_V070004.bin",
        "MIN_VERSION": "V030071",
        "MODEL": ""
      }
    ],
    "DSPS": [
      {
        "CHIP": "",
        "LATEST_VERSION": "V070004",
        "LINK": "https://firmware-version-bucket.s3.eu-west-1.amazonaws.com/ZM2/DSPS/HYD-EP_DSPS_V070004.bin",
        "MIN_VERSION": "V030000",
        "MODEL": ""
      }
    ],
    "MODE": [
      "LAN",
      "DASHBOARD"
    ],
    "SAFETY": [
      {
        "CHIP": "",
        "LATEST_VERSION": "",
        "LINK": "https://firmware-version-bucket.s3.eu-west-1.amazonaws.com/ZM2/SAFETY/HYD-EP_SAFETY_250303090509.bin",
        "MIN_VERSION": "",
        "MODEL": ""
      }
    ]
  },
  "^z.1063.{14}$": {
    "ID": "Z*1063",
    "MIN_ONE_CLICK_ARM": "V000002",
    "MIN_SGP_DSPM": "V000006",
    "MIN_SGP_DSPS": "V000006",
    "MIN_SGP_ARM": "V000006",
    "ARM": [],
    "DEVICE_MODEL_ID": "INVERTER",
    "DSPM": [],
    "DSPS": [],
    "MODE": [
      "LAN",
      "DASHBOARD"
    ],
    "SAFETY": []
  }
};
